"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const Disease_1 = __importDefault(require("@becrop/components/Toolkit/Heatmap2/models/report/Disease"));
const PGP_1 = __importDefault(require("@becrop/components/Toolkit/Heatmap2/models/report/PGP"));
const NutrientFunction_1 = __importDefault(require("@becrop/components/Toolkit/Heatmap2/models/report/NutrientFunction"));
const trans_1 = require("@common/tsutils/trans");
class Rank {
    constructor(name) {
        this.values = [];
        this.name = name;
    }
    isAllNull() {
        const numNulls = this.values.reduce((acc, val) => {
            return acc + (!val ? 1 : 0);
        }, 0);
        return numNulls === this.values.length;
    }
    static newReportRank(name, flatSamples, path) {
        const r = new Rank(name);
        r.values = flatSamples.map(fs => {
            return fs.sample.report.ranks[path];
        });
        return r;
    }
    static newDiseaseRank(disease, flatSamples) {
        const r = new Rank(disease.name);
        r.values = flatSamples.map(fs => {
            const rdis = fs.sample.report.diseases.find(d => d.disease.code_name === disease.code_name);
            let rank;
            if (!rdis) {
                rank = null;
            }
            else {
                if (!rdis.detected) {
                    rank = 0;
                }
                else {
                    rank = rdis.rank;
                }
            }
            return rank;
        });
        return r;
    }
    static newPGPRank(pgp, flatSamples) {
        const r = new Rank(pgp.name);
        r.values = flatSamples.map(fs => {
            const rpgp = fs.sample.report.grow_promoters.find(g => g.pgp.code_name === pgp.code_name);
            let rank;
            if (!rpgp) {
                rank = null;
            }
            else {
                if (!rpgp.detected) {
                    rank = 0;
                }
                else {
                    rank = rpgp.rank;
                }
            }
            return rank;
        });
        return r;
    }
    static newNutrientFunctionRank(nutF, flatSamples) {
        const r = new Rank(nutF.name);
        r.values = flatSamples.map(fs => {
            const rnutf = fs.sample.report.nutrient_functions.find(n => n.nutrientFunction.code_name === nutF.code_name);
            return rnutf ? rnutf.rank : null;
        });
        return r;
    }
}
class HeatmapRanks {
    constructor(flatSamples) {
        this.ranks = [];
        this.colorStyle = 'standard';
        this.flatSamples = flatSamples;
    }
    addRank(name, path) {
        this.ranks.push(Rank.newReportRank(name, this.flatSamples, path));
    }
    addBioRanks() {
        this.addRank((0, trans_1.trans)('becrop_report.biosustainability_items.soil_quality'), 'shi');
        this.addRank((0, trans_1.trans)('becrop_report.biosustainability_items.biodiversity'), 'biodiversity');
        this.addRank((0, trans_1.trans)('becrop_report.biosustainability_items.functionality'), 'functionality');
        this.addRank((0, trans_1.trans)('becrop_report.biosustainability_items.resistance'), 'resistance');
        this.addRank((0, trans_1.trans)('becrop_report.biosustainability_items.resilience'), 'resilience');
    }
    addDiseaseRanks() {
        const diseases = Disease_1.default.all();
        let ranks = diseases.map(disease => {
            return Rank.newDiseaseRank(disease, this.flatSamples);
        });
        ranks.forEach(r => this.ranks.push(r));
        this.colorStyle = 'health';
    }
    addPGPRanks(types) {
        const pgps = PGP_1.default.all();
        let ranks = pgps.filter(pgp => {
            return types.includes(pgp.type_code_name);
        }).map(pgp => {
            return Rank.newPGPRank(pgp, this.flatSamples);
        });
        ranks.forEach(r => this.ranks.push(r));
    }
    addNutrientFunctionRanks(symbols) {
        const nutrientFunctions = NutrientFunction_1.default.all();
        symbols.forEach(symbol => {
            let ranks = nutrientFunctions.filter(nfunct => {
                return symbol === nfunct.nutrient_symbol;
            }).map(nfunct => {
                return Rank.newNutrientFunctionRank(nfunct, this.flatSamples);
            });
            ranks.forEach(r => this.ranks.push(r));
        });
    }
    addNutrientRanks() {
        const dict = {};
        const nutrientFunctions = [];
        NutrientFunction_1.default.all().forEach(nt => {
            if (dict[nt.nutrient_symbol])
                return;
            dict[nt.nutrient_symbol] = true;
            nutrientFunctions.push(nt);
        });
        let ranks = nutrientFunctions.map(nfunct => {
            const rank = Rank.newNutrientFunctionRank(nfunct, this.flatSamples);
            rank.name = nfunct.nutrient_symbol;
            return rank;
        });
        ranks.forEach(r => this.ranks.push(r));
    }
    /**
     * Get the values of just one sample
     * @param sample
     */
    getValues(sample) {
        const idx = this.flatSamples.map(fs => fs.sample).indexOf(sample);
        return this.ranks.map(r => r.values[idx]);
    }
    static build(root, type) {
        const ranks = new HeatmapRanks(root.flatSamples());
        if (type === 'all') {
            ranks.addBioRanks();
            ranks.addPGPRanks(['BIO', 'STR', 'HOM']);
            ranks.addNutrientRanks();
        }
        else if (type === 'bio') { // Sustainability
            ranks.addBioRanks();
        }
        else if (type === 'hlt') { // Health
            ranks.addDiseaseRanks();
        }
        else if (type === 'pb') { // Biocontrol
            ranks.addPGPRanks(['BIO']);
        }
        else if (type === 'pe') { // Stress and Hormones
            ranks.addPGPRanks(['STR', 'HOM']);
        }
        else if (type === 'c') { // Carbon
            ranks.addNutrientFunctionRanks(['C']);
        }
        else if (type === 'npk') { // N, P, K
            ranks.addNutrientFunctionRanks(['N', 'P', 'K']);
        }
        else if (type === 'nt') { // Minor
            ranks.addNutrientRanks();
        }
        return ranks;
    }
}
exports.default = HeatmapRanks;
